import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

type Tone = 'default' | 'success' | 'warning' | 'destructive';

const valueTone: Record<Tone, string> = {
  default: 'text-foreground',
  success: 'text-success',
  warning: 'text-warning',
  destructive: 'text-destructive',
};

const iconTone: Record<Tone, string> = {
  default: 'bg-primary/10 text-primary',
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/15 text-warning',
  destructive: 'bg-destructive/10 text-destructive',
};

export function StatTile({
  label,
  value,
  sub,
  tone = 'default',
  icon: Icon,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: Tone;
  icon?: LucideIcon;
}) {
  return (
    <div className="rounded-lg border bg-card p-4 shadow-[0_1px_2px_0_rgb(15_23_42/0.04)] animate-in fade-in-0 slide-in-from-bottom-1 duration-300">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {label}
          </p>
          <p className={cn('mt-1 text-2xl font-semibold tabular', valueTone[tone])}>{value}</p>
        </div>
        {Icon && (
          <span
            className={cn('grid h-9 w-9 shrink-0 place-items-center rounded-lg', iconTone[tone])}
          >
            <Icon className="h-[18px] w-[18px]" aria-hidden />
          </span>
        )}
      </div>
      {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}
